// FAQ/홈 설정이 공유하는 draft→검증→배포→롤백 CMS의 콘텐츠 타입과 기본 shape.
// apps/admin/src/lib/cms.js(쓰기), packages/validation/cms.js(검증), firestore.rules(서버측
// 게이트)가 전부 이 상수를 기준으로 삼는다.

export const CMS_CONTENT_TYPES = ['faq', 'homeConfig', 'buildings', 'facilities', 'cafeteriaOverrides', 'noticeOverrides', 'featureSettings']

export const FAQ_SCHEMA_VERSION = 'faq.v1'
export const HOME_CONFIG_SCHEMA_VERSION = 'home.v1'
export const BUILDINGS_SCHEMA_VERSION = 'buildings.v1'
export const FACILITIES_SCHEMA_VERSION = 'facilities.v1'
export const CAFETERIA_OVERRIDES_SCHEMA_VERSION = 'cafeteriaOverrides.v1'
export const NOTICE_OVERRIDES_SCHEMA_VERSION = 'noticeOverrides.v1'
export const FEATURE_SETTINGS_SCHEMA_VERSION = 'featureSettings.v1'

export const FACILITY_CATEGORIES = ['shuttle', 'parking', 'store', 'cafeteria', 'cafe-store', 'printer']

export const CMS_HOME_CARD_IDS = {
  todayQuickChips: ['cafeteria', 'shuttle', 'sugiInstant'],
  classicUtilityRow: ['notice', 'credit', 'report'],
}

// disabledFeatures는 todayQuickChips/classicUtilityRow와 같은 id 공간을 공유하고,
// 추가로 행 단위 카드(classicMealMapRow/classicSugiInstantCard)도 비활성 표시 대상이 될 수 있다.
export const CMS_HOME_DISABLEABLE_IDS = [
  ...CMS_HOME_CARD_IDS.todayQuickChips,
  ...CMS_HOME_CARD_IDS.classicUtilityRow,
]

export const EMPTY_FAQ_ITEM = {
  id: '', question: '', answer: '', category: '', keywords: [],
  order: 0, active: true,
  intent: '', confidence: 'medium', officialUrl: '', relatedLinks: [], relatedQuestions: [],
  source: '', year: '', semester: '',
}

export const EMPTY_HOME_CONFIG_DATA = {
  schemaVersion: HOME_CONFIG_SCHEMA_VERSION,
  hero: { mode: 'auto' },
  maintenanceMessage: { enabled: false, message: '' },
  cards: {
    todayQuickChips: [
      { id: 'cafeteria', visible: true, order: 0 },
      { id: 'shuttle', visible: true, order: 1 },
      { id: 'sugiInstant', visible: true, order: 2 },
    ],
    classicMealMapRow: { visible: true },
    classicSugiInstantCard: { visible: true },
    classicUtilityRow: [
      { id: 'notice', visible: true, order: 0 },
      { id: 'credit', visible: true, order: 1 },
      { id: 'report', visible: true, order: 2 },
    ],
  },
  disabledFeatures: [],
}

export const EMPTY_FAQ_DATA = {
  schemaVersion: FAQ_SCHEMA_VERSION,
  items: [],
}

// 건물의 admin 관리 필드 상위집합(전체 필드는 src/data/campusBuildings.js 참고). 앱 런타임
// 어댑터는 이 필드만 정적 레코드 위에 덮어쓴다(icon/keywords/floors 등 CMS가 다루지 않는
// 필드는 그대로 유지 — 전체 교체가 아니라 필드 단위 병합).
export const EMPTY_BUILDING_ITEM = {
  id: '', name: '', category: '', lat: '', lng: '',
  status: 'confirmed', description: '', phone: '',
  entranceLat: '', entranceLng: '', entranceLabel: '', entranceStatus: 'pending',
  photoUrl: '', active: true,
}

export const EMPTY_FACILITY_ITEM = {
  id: '', category: 'parking', name: '', lat: '', lng: '',
  description: '', status: 'confirmed', photoUrl: '', active: true,
}

export const EMPTY_BUILDINGS_DATA = { schemaVersion: BUILDINGS_SCHEMA_VERSION, items: [] }
export const EMPTY_FACILITIES_DATA = { schemaVersion: FACILITIES_SCHEMA_VERSION, items: [] }

// 학식/공지는 기존 크롤러 + public JSON 배포 구조를 그대로 유지한다 — 여기는 그 위에 얹는
// "override 레이어"만 관리한다. restaurantId+date가 일치하는 override가 있으면 크롤러 원본
// 대신 이 값을 쓰고, 없으면(active:false 포함) 크롤러 원본을 그대로 쓴다.
export const EMPTY_CAFETERIA_OVERRIDE_ITEM = {
  id: '', restaurantId: '', date: '', breakfast: [], lunch: [], dinner: [],
  closed: false, note: '', active: true,
}

export const EMPTY_CAFETERIA_COPY = {
  mealEmptyTitle: '', mealEmptyDescription: '', weeklyEmptyTitle: '', weeklyEmptyDescription: '', dataErrorMessage: '',
}

// sourceNoticeId가 있으면 그 크롤러 공지의 표시값을 덮어쓴다(비우면 크롤러 원본 유지, visible:false면
// 숨김). sourceNoticeId가 없으면 admin이 직접 작성한 새 공지로 취급해 목록에 추가한다.
export const EMPTY_NOTICE_OVERRIDE_ITEM = {
  id: '', sourceNoticeId: '', title: '', category: '', date: '', url: '', summary: '',
  important: false, visible: true, active: true,
}

export const EMPTY_CAFETERIA_OVERRIDES_DATA = { schemaVersion: CAFETERIA_OVERRIDES_SCHEMA_VERSION, items: [], copy: { ...EMPTY_CAFETERIA_COPY } }
export const EMPTY_NOTICE_OVERRIDES_DATA = { schemaVersion: NOTICE_OVERRIDES_SCHEMA_VERSION, items: [] }

// 앱은 이 값을 읽고 조회 실패/미배포 시 반드시 이 기본값(전부 "정상 운영")으로 동작해야 한다 —
// maintenanceMode.enabled나 forceUpdate가 실수로 true가 기본값이 되면 전체 사용자가 앱에서
// 차단될 수 있다. platforms는 ios/android만 다룬다(web은 강제 업데이트 개념이 없음).
export const EMPTY_FEATURE_SETTINGS_DATA = {
  schemaVersion: FEATURE_SETTINGS_SCHEMA_VERSION,
  maintenanceMode: { enabled: false, message: '' },
  operationalMessage: { enabled: false, message: '' },
  // Friends stays fail-closed until the next release explicitly enables it.
  featureToggles: { friends: false },
  minVersion: { ios: '', android: '' },
  forceUpdate: { ios: false, android: false, message: '' },
}

export function emptyCmsData(contentType) {
  if (contentType === 'faq') return { ...EMPTY_FAQ_DATA }
  if (contentType === 'homeConfig') return JSON.parse(JSON.stringify(EMPTY_HOME_CONFIG_DATA))
  if (contentType === 'buildings') return { ...EMPTY_BUILDINGS_DATA }
  if (contentType === 'facilities') return { ...EMPTY_FACILITIES_DATA }
  if (contentType === 'cafeteriaOverrides') return { ...EMPTY_CAFETERIA_OVERRIDES_DATA }
  if (contentType === 'noticeOverrides') return { ...EMPTY_NOTICE_OVERRIDES_DATA }
  if (contentType === 'featureSettings') return JSON.parse(JSON.stringify(EMPTY_FEATURE_SETTINGS_DATA))
  throw new Error(`unknown cms contentType: ${contentType}`)
}
